import { FinancialAccountType } from "./types/financialAccount";

export enum DocumentTaxonomy {
    // Top-level categories
    tax = "tax",
    receipts = "receipts",
    account_statements = "account_statements",
    loan_statements = "loan_statements",
    lease_statements = "lease_statements",
    formation_documents = "formation_documents",
    legal_documents = "legal_documents",
    accounts_receivable_payable = "accounts_receivable_payable",
    financial_statements = "financial_statements",
    payroll = "payroll",
    checks = "checks",
    assets = "assets",
    transaction_records = "transaction_records",
    uncategorized = "uncategorized",
    other = "other",

    // Tax subcategories
    tax_return = "tax_return",
    form_1099 = "form_1099",
    form_w9 = "form_w9",
    mileage = "mileage",
    home_office = "home_office",
    estimated_taxes = "estimated_taxes",

    // Account Statements subcategories
    bank_statement = "bank_statement",
    credit_card_statement = "credit_card_statement",
    payment_processor_statement = "payment_processor_statement",
    investment_account_statement = "investment_account_statement",

    // Loan Statements subcategories
    vehicle_loan_statement = "vehicle_loan_statement",
    mortgage_loan_statement = "mortgage_loan_statement",
    working_capital_loan_statement = "working_capital_loan_statement",
    line_of_credit_statement = "line_of_credit_statement",
    merchant_cash_advance_statement = "merchant_cash_advance_statement",
    loan_agreement = "loan_agreement",
    loan_payment_schedule = "loan_payment_schedule",

    // Formation Documents subcategories
    partnership_agreement = "partnership_agreement",
    articles_of_incorporation = "articles_of_incorporation",

    // Legal Documents subcategories
    operating_agreement = "operating_agreement",
    contract = "contract",

    // Accounts Receivable & Payable subcategories
    invoice = "invoice",
    customer_information = "customer_information",
    bill = "bill",
    vendor_information = "vendor_information",

    // Financial Statements subcategories
    balance_sheet = "balance_sheet",
    profit_and_loss = "profit_and_loss",
    cash_flow_statement = "cash_flow_statement",
    general_ledger = "general_ledger",
    trial_balance = "trial_balance",
    revenue_schedules = "revenue_schedules",
    chart_of_accounts = "chart_of_accounts",
    bank_reconciliation = "bank_reconciliation",

    // Payroll subcategories
    form_w3 = "form_w3",
    form_w2 = "form_w2",
    form_940 = "form_940",
    form_941 = "form_941",
    payroll_journal_report = "payroll_journal_report",

    // Assets subcategories
    purchase_and_sale_agreement = "purchase_and_sale_agreement",
    depreciation_schedule = "depreciation_schedule",
    amortization_schedule = "amortization_schedule",

    // Other subcategories
    correspondence = "correspondence",
    miscellaneous = "miscellaneous",
    backup = "backup",
}

export type DocumentTaxonomyMainCategories = Extract<
    DocumentTaxonomy,
    | DocumentTaxonomy.tax
    | DocumentTaxonomy.receipts
    | DocumentTaxonomy.account_statements
    | DocumentTaxonomy.loan_statements
    | DocumentTaxonomy.lease_statements
    | DocumentTaxonomy.formation_documents
    | DocumentTaxonomy.legal_documents
    | DocumentTaxonomy.accounts_receivable_payable
    | DocumentTaxonomy.financial_statements
    | DocumentTaxonomy.payroll
    | DocumentTaxonomy.checks
    | DocumentTaxonomy.assets
    | DocumentTaxonomy.transaction_records
    | DocumentTaxonomy.other
>;

export const DOCUMENT_TAXONOMY_MAIN_TO_SUB: Record<
    DocumentTaxonomyMainCategories,
    DocumentTaxonomy[]
> = {
    [DocumentTaxonomy.tax]: [
        DocumentTaxonomy.tax_return,
        DocumentTaxonomy.form_1099,
        DocumentTaxonomy.form_w9,
        DocumentTaxonomy.mileage,
        DocumentTaxonomy.home_office,
        DocumentTaxonomy.estimated_taxes,
    ],
    [DocumentTaxonomy.receipts]: [],
    [DocumentTaxonomy.account_statements]: [
        DocumentTaxonomy.bank_statement,
        DocumentTaxonomy.credit_card_statement,
        DocumentTaxonomy.payment_processor_statement,
        DocumentTaxonomy.investment_account_statement,
    ],
    [DocumentTaxonomy.loan_statements]: [
        DocumentTaxonomy.vehicle_loan_statement,
        DocumentTaxonomy.mortgage_loan_statement,
        DocumentTaxonomy.working_capital_loan_statement,
        DocumentTaxonomy.line_of_credit_statement,
        DocumentTaxonomy.merchant_cash_advance_statement,
        DocumentTaxonomy.loan_agreement,
        DocumentTaxonomy.loan_payment_schedule,
    ],
    [DocumentTaxonomy.lease_statements]: [],
    [DocumentTaxonomy.formation_documents]: [
        DocumentTaxonomy.partnership_agreement,
        DocumentTaxonomy.articles_of_incorporation,
    ],
    [DocumentTaxonomy.legal_documents]: [
        DocumentTaxonomy.operating_agreement,
        DocumentTaxonomy.contract,
    ],
    [DocumentTaxonomy.accounts_receivable_payable]: [
        DocumentTaxonomy.invoice,
        DocumentTaxonomy.customer_information,
        DocumentTaxonomy.bill,
        DocumentTaxonomy.vendor_information,
    ],
    [DocumentTaxonomy.financial_statements]: [
        DocumentTaxonomy.balance_sheet,
        DocumentTaxonomy.profit_and_loss,
        DocumentTaxonomy.cash_flow_statement,
        DocumentTaxonomy.general_ledger,
        DocumentTaxonomy.trial_balance,
        DocumentTaxonomy.revenue_schedules,
        DocumentTaxonomy.chart_of_accounts,
        DocumentTaxonomy.bank_reconciliation,
    ],
    [DocumentTaxonomy.payroll]: [
        DocumentTaxonomy.form_w3,
        DocumentTaxonomy.form_w2,
        DocumentTaxonomy.form_940,
        DocumentTaxonomy.form_941,
        DocumentTaxonomy.payroll_journal_report,
    ],
    [DocumentTaxonomy.checks]: [],
    [DocumentTaxonomy.assets]: [
        DocumentTaxonomy.purchase_and_sale_agreement,
        DocumentTaxonomy.depreciation_schedule,
        DocumentTaxonomy.amortization_schedule,
    ],
    [DocumentTaxonomy.transaction_records]: [],
    [DocumentTaxonomy.other]: [
        DocumentTaxonomy.correspondence,
        DocumentTaxonomy.miscellaneous,
        DocumentTaxonomy.backup,
    ],
};

const DOCUMENT_TAXONOMY_SUB_TO_MAIN: Partial<
    Record<DocumentTaxonomy, DocumentTaxonomyMainCategories>
> = Object.fromEntries(
    Object.entries(DOCUMENT_TAXONOMY_MAIN_TO_SUB).flatMap(([main, subs]) =>
        subs.map((sub) => [sub, main as DocumentTaxonomyMainCategories]),
    ),
);

export function getMainDocumentTaxonomy(
    taxonomy: DocumentTaxonomy,
): DocumentTaxonomyMainCategories {
    return (
        DOCUMENT_TAXONOMY_SUB_TO_MAIN[taxonomy] ??
        (taxonomy as DocumentTaxonomyMainCategories)
    );
}

export const RECEIPT_MATCHABLE_TAXONOMIES = new Set([
    DocumentTaxonomy.receipts,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.receipts],
]);

export const LOAN_TAXONOMIES = new Set([
    DocumentTaxonomy.loan_statements,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.loan_statements],
]);

export const ACCOUNT_MATCHABLE_TAXONOMIES = new Set([
    DocumentTaxonomy.account_statements,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.account_statements],
    DocumentTaxonomy.transaction_records,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.transaction_records],
    DocumentTaxonomy.loan_statements,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.loan_statements],
    DocumentTaxonomy.lease_statements,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.lease_statements],
    DocumentTaxonomy.checks,
    ...DOCUMENT_TAXONOMY_MAIN_TO_SUB[DocumentTaxonomy.checks],
]);

const TAXONOMY_TO_ACCOUNT_TYPES: Partial<
    Record<DocumentTaxonomy, FinancialAccountType[]>
> = {
    [DocumentTaxonomy.bank_statement]: [
        FinancialAccountType.CHECKING,
        FinancialAccountType.SAVINGS,
    ],
    [DocumentTaxonomy.credit_card_statement]: [
        FinancialAccountType.CREDIT_CARD,
    ],
    [DocumentTaxonomy.payment_processor_statement]: [
        FinancialAccountType.PAYMENT_PROCESSOR,
    ],
    [DocumentTaxonomy.investment_account_statement]: [
        FinancialAccountType.INVESTMENT,
    ],
    [DocumentTaxonomy.loan_statements]: [FinancialAccountType.LOAN],
    [DocumentTaxonomy.vehicle_loan_statement]: [FinancialAccountType.LOAN],
    [DocumentTaxonomy.mortgage_loan_statement]: [FinancialAccountType.LOAN],
    [DocumentTaxonomy.working_capital_loan_statement]: [
        FinancialAccountType.LOAN,
    ],
    [DocumentTaxonomy.line_of_credit_statement]: [
        FinancialAccountType.LINE_OF_CREDIT,
    ],
    [DocumentTaxonomy.merchant_cash_advance_statement]: [
        FinancialAccountType.LOAN,
    ],
    [DocumentTaxonomy.loan_agreement]: [FinancialAccountType.LOAN],
    [DocumentTaxonomy.loan_payment_schedule]: [FinancialAccountType.LOAN],
};

const ACCOUNT_TYPE_TO_STATEMENT_TAXONOMY: Partial<
    Record<FinancialAccountType, DocumentTaxonomy>
> = {
    [FinancialAccountType.CHECKING]: DocumentTaxonomy.bank_statement,
    [FinancialAccountType.SAVINGS]: DocumentTaxonomy.bank_statement,
    [FinancialAccountType.CREDIT_CARD]: DocumentTaxonomy.credit_card_statement,
    [FinancialAccountType.LOAN]: DocumentTaxonomy.loan_statements,
    [FinancialAccountType.LINE_OF_CREDIT]:
        DocumentTaxonomy.line_of_credit_statement,
    [FinancialAccountType.INVESTMENT]:
        DocumentTaxonomy.investment_account_statement,
    [FinancialAccountType.PAYMENT_PROCESSOR]:
        DocumentTaxonomy.payment_processor_statement,
};

export function getStatementTaxonomyForAccountType(
    accountType: FinancialAccountType,
): DocumentTaxonomy | null {
    return ACCOUNT_TYPE_TO_STATEMENT_TAXONOMY[accountType] ?? null;
}

export function getExpectedAccountTypes(
    taxonomy: DocumentTaxonomy,
): FinancialAccountType[] | undefined {
    return TAXONOMY_TO_ACCOUNT_TYPES[taxonomy];
}
